import {
    ActionIcon,
    Button,
    Card,
    Center,
    Divider,
    Group,
    PasswordInput,
    Radio,
    rem,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { useForm } from "@mantine/form";
import { IconFileDescription, IconFileInfo, IconX } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { zodResolver } from "mantine-form-zod-resolver";
import { useState } from "react";
import { Content } from "shared/components/global/Content";
import { Header } from "shared/components/global/Header";
import { CertificateRoleAssignPanel } from "shared/components/ipaws/certificate/CertificateRoleAssignPanel";
import { UserSchema } from "shared/components/organization/users/data";
import { useDirtyFormBlocker } from "shared/hooks/useDirtyFormBlocker";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { fileToBase64, to } from "shared/utils/fns";
import { maybe } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import * as zx from "shared/utils/zod";
import { graphql } from "src/gql";
import { IpawsCertificateType } from "src/gql/graphql";
import z from "zod";

const createFormSchema = z.object({
    name: zx.nonemptyString(),
    description: zx.nonemptyString(),
    type: z.nativeEnum(IpawsCertificateType),
    keystoreB64: z.string().base64(),
    keystorePassword: zx.nonemptyString(),
    keystoreAliasPassword: zx.nonemptyString(),
    cogId: zx.nonemptyString(),
});

type CreateFormSchema = z.infer<typeof createFormSchema>;

const FileDropzone = ({ onFileSubmit }: { onFileSubmit: (fileName: string, base64str: string) => void }) => {
    return (
        <Dropzone
            accept={{ "application/x-java-keystore": [".jks"] }}
            onDrop={(file) =>
                maybe(file.at(0))?.take(
                    (it) =>
                        void fileToBase64(it)
                            .then((res) => onFileSubmit(it.name, res))
                            .catch(notify.catch),
                )
            }
            inputProps={{ "aria-label": "Upload file" }}
        >
            <Stack align="center" gap={"xs"} py="xl">
                <Dropzone.Accept>
                    <IconFileDescription style={{ width: rem(42), height: rem(42), color: "dimmed" }} stroke={1.5} />
                </Dropzone.Accept>
                <Dropzone.Reject>
                    <IconFileDescription style={{ width: rem(42), height: rem(42), color: "dimmed" }} stroke={1.5} />
                </Dropzone.Reject>
                <Dropzone.Idle>
                    <IconFileDescription style={{ width: rem(42), height: rem(42), color: "dimmed" }} stroke={1.5} />
                </Dropzone.Idle>

                <div style={{ textAlign: "center" }}>
                    <Text size="lg" inline>
                        Attach Keystore File
                    </Text>
                    <Text size="sm" inline mt={7}>
                        .jks
                    </Text>
                </div>
            </Stack>
        </Dropzone>
    );
};

const FilePreview = ({ fileName, onCancel }: { fileName: string; onCancel: () => void }) => (
    <Card shadow="sm" p="sm" pb="xs" w="fit-content">
        <Card.Section component="div" style={{ border: "unset", cursor: "unset" }}>
            <Center>
                <Center w={rem(100)} h={rem(100)}>
                    <IconFileInfo size={42} />
                </Center>
            </Center>
        </Card.Section>
        <Group gap={0}>
            <Text size="xs">{fileName}</Text>
            <ActionIcon variant="transparent" onClick={onCancel}>
                <IconX size={16} />
            </ActionIcon>
        </Group>
    </Card>
);

const Page = () => {
    const nav = Route.useNavigate();
    const [selectedFileName, setSelectedFileName] = useState<string>();

    const createCert = useGraphqlMutation(muts.createCertificate);

    const [selectedUsers, setSelectedUsers] = useState<UserSchema[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<UserSchema["roles"][0]["role"][]>([]);
    const canAssignRoles = selectedRoles.length > 0 && selectedUsers.length > 0;

    const form = useForm<CreateFormSchema>({
        mode: "uncontrolled",
        initialValues: {
            name: "",
            description: "",
            type: "" as IpawsCertificateType,
            keystorePassword: "",
            keystoreAliasPassword: "",
            cogId: "",
            keystoreB64: "",
        },
        validate: zodResolver(createFormSchema),
    });

    useDirtyFormBlocker(form);

    return (
        <Content paper>
            <Content.Heading backable>
                <Title order={3}>Add Certificate</Title>
            </Content.Heading>
            <form
                onSubmit={form.onSubmit((vals) => {
                    createCert
                        .mutateAsync({
                            params: {
                                ...vals,
                                assignRoles: canAssignRoles
                                    ? selectedRoles.map((r) => ({ roleId: r.id, userIds: selectedUsers.map(to("id")) }))
                                    : undefined,
                                boundaries: [],
                            },
                        })
                        .then(() => {
                            form.reset();
                            setSelectedUsers([]);
                            setSelectedRoles([]);
                            notify.show.success("Created certificate");
                            nav({ to: "/ipaws/certificates" }).catch(notify.catch);
                        })
                        .catch(notify.catch);
                })}
                autoComplete="off"
            >
                <Stack>
                    <Header>IPAWS Profile</Header>
                    <TextInput label="Name" required placeholder="Enter name..." {...form.getInputProps("name")} autoComplete="off" />
                    <Textarea label="Description" required placeholder="Enter description..." {...form.getInputProps("description")} />
                    <Radio.Group
                        label="Certificate Environment"
                        description="This selection cannot be updated."
                        required
                        {...form.getInputProps("type")}
                    >
                        <Group mt="xs">
                            {Object.values(IpawsCertificateType).map((type) => (
                                <Radio key={type} value={type} label={type} />
                            ))}
                        </Group>
                    </Radio.Group>
                    <Header>Certificate</Header>
                    {!selectedFileName ? (
                        <FileDropzone
                            onFileSubmit={(fileName, str) => {
                                form.setFieldValue("keystoreB64", str);
                                setSelectedFileName(fileName);
                            }}
                        />
                    ) : (
                        <FilePreview
                            fileName={selectedFileName}
                            onCancel={() => {
                                setSelectedFileName(undefined);
                                form.resetField("keystoreB64");
                            }}
                        />
                    )}
                    <PasswordInput
                        label="Keystore Password"
                        required
                        placeholder="Enter password..."
                        {...form.getInputProps("keystorePassword")}
                        autoComplete="new-password"
                    />
                    <PasswordInput
                        label="Key Password"
                        required
                        placeholder="Enter password..."
                        {...form.getInputProps("keystoreAliasPassword")}
                        autoComplete="new-password"
                    />
                    <TextInput label="COG ID" required placeholder="Enter ID..." {...form.getInputProps("cogId")} />
                    <Header>Assign User Roles</Header>
                    <CertificateRoleAssignPanel
                        usersProps={{ selectedUsers, setSelectedUsers }}
                        rolesProps={{ selectedRoles, setSelectedRoles }}
                    />
                    <Divider />
                    <Group justify="flex-end">
                        <Link to="/ipaws/certificates">
                            <Button variant="default">Cancel</Button>
                        </Link>
                        <Button type="submit" disabled={!form.isValid()} loading={createCert.isPending}>
                            Save
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Content>
    );
};

/** @public */
export const Route = createFileRoute("/_auth/ipaws/certificates/create")({
    component: Page,
});

const muts = {
    createCertificate: graphql(`
        mutation IpawsCertCreate($params: CreateIpawsCertInfoInput!) {
            createIpawsCertificate(params: $params) {
                id
            }
        }
    `),
};
